/**
 * Registre des séries temporelles : une clé stable → un libellé i18n et une
 * couleur FIXE (m-12, BUG-2).
 *
 * La couleur suit la série, jamais sa position dans les données : filtrer sur
 * « Parcours » garde l'orange du Parcours, et deux séries différentes d'un même
 * graphique n'ont jamais la même teinte.
 *
 * Clé d'une série : `metric`, puis `/<valeur>` pour chaque dimension, puis
 * `/<devise>` pour une série monétaire (`IA-01/chat`, `FIN-12/MAD`).
 */
import type { Series } from '../../../api/monitoring/v2';
import { MONITORING_PALETTE as P, STATE_COLORS } from '../monitoringTheme';

export type SeriesMeta = { labelKey: string; color: string };

const SOURCE_COLORS = {
    chat: P.blue,
    learn: P.orange,
    embed: P.green,
    audio: P.violet,
} as const;

const bySource = (metric: string, suffix = ''): Record<string, SeriesMeta> =>
    Object.fromEntries(
        Object.entries(SOURCE_COLORS).map(([source, color]) => [
            `${metric}/${source}${suffix}`,
            { labelKey: `monitoring.enum.ai_source.${source}`, color },
        ])
    );

export const SERIES_REGISTRY: Record<string, SeriesMeta> = {
    // Pédagogie
    'PED-03': { labelKey: 'monitoring.series.students_active', color: P.violet },
    'PED-05': { labelKey: 'monitoring.series.lessons_completed', color: P.blue },
    'PED-06': { labelKey: 'monitoring.series.exercises_submitted', color: P.orange },
    'PED-07': { labelKey: 'monitoring.series.projects_created', color: P.green },
    'PED-08': { labelKey: 'monitoring.series.sessions', color: P.teal },
    'PED-09': { labelKey: 'monitoring.series.active_minutes', color: P.ochre },
    // IA : une couleur par service, identique pour les appels et les coûts.
    ...bySource('IA-01'),
    // Coûts IA : en USD (D-16) ; une autre devise ne serait pas confondue avec eux.
    ...bySource('IA-02', '/USD'),
    'IA-05': { labelKey: 'monitoring.series.questions', color: P.teal },
    // Financier : une série par devise, jamais additionnées.
    'FIN-12/MAD': { labelKey: 'monitoring.series.currency.MAD', color: P.green },
    'FIN-12/EUR': { labelKey: 'monitoring.series.currency.EUR', color: P.blue },
    'FIN-12/USD': { labelKey: 'monitoring.series.currency.USD', color: P.slate },
};

/** Clé de registre d'une série reçue du serveur. */
export const seriesKey = (series: Series): string => {
    const parts = [series.metric];
    if (series.dimension) {
        Object.entries(series.dimension)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([, value]) => {
                if (value !== undefined) parts.push(value);
            });
    }
    if (series.currency) parts.push(series.currency);
    return parts.join('/');
};

/** Teinte des séries absentes du registre : visible, jamais celle d'une autre série. */
export const UNREGISTERED_COLOR = STATE_COLORS.neutral;

/**
 * Métadonnées d'une série. Une clé inconnue n'est pas masquée : elle garde sa
 * clé brute comme libellé, une teinte neutre, et un avertissement en console
 * pour que le registre soit complété.
 */
export const seriesMeta = (key: string): SeriesMeta & { registered: boolean } => {
    const meta = SERIES_REGISTRY[key];
    if (meta) return { ...meta, registered: true };
    // eslint-disable-next-line no-console
    console.warn('[monitoring] série absente du registre', key);
    return { labelKey: key, color: UNREGISTERED_COLOR, registered: false };
};
